import React from "react";
import { Route, Switch } from "react-router-dom";

import * as QueriesQL from "queries/bucket";
import BucketPublicSchema from "@common/schema/bucket/public";
import BucketFilePublicSchema from "@common/schema/bucket-file/public";
import CapturePublicSchema from "@common/schema/capture/public";
import * as Section from "@repoflow/components/section";
import { Crumb, useCrumbLabel } from "@repoflow/components/breadcrumb";

import * as ViewRoute from "./view";
import * as EditRoute from "./edit";
import * as RemoveRoute from "./remove";
import * as FilesRoute from "./files";
import * as StatisticsRoute from "./statistics";

const LocalProvider = ({
  match: {
    params,
    params: { bucketId },
  },
  children: Children,
}) => {
  const { base: parentBase, basepath: parentBasepath } =
    Section.useSectionContext();

  const base = `${parentBase}/${bucketId}`;
  const basepath = `${parentBasepath}/:bucketId`;

  // Keyed on the entity, which is what the accidental remount used to do.
  //
  // Every screen below seeds its form state ONCE, from the entity this
  // route resolved. Until the render prop above was called rather than
  // rendered, React saw a new component type on every render and remounted
  // the lot — which reset those forms, and happened to be what made moving
  // from one bucketId to another show the new one's values. It no longer
  // remounts for free, so the reset it was providing is asked for here.
  return (
    <Section.Provider
      key={bucketId}
      schema={[
        ...BucketPublicSchema,
        ...BucketFilePublicSchema,
        ...CapturePublicSchema,
      ]}
      base={base}
      basepath={basepath}
      attributes={params}
    >
      {/* Called, never rendered as an element — see Section.Provider. */}
      {Children({ base, basepath, params })}
    </Section.Provider>
  );
};

export function Content(props) {
  return (
    <LocalProvider {...props}>
      {({ params }) => (
        <Section.Query
          query={QueriesQL.get}
          variables={params}
          datapath={"buckets.get"}
        >
          {ContentHandler}
        </Section.Query>
      )}
    </LocalProvider>
  );
}

function ContentHandler({ basepath }) {
  return (
    <Switch>
      <Route path={`${basepath}/file`} component={FilesRoute.Content} />
      <Route path={`${basepath}/statistics`} component={StatisticsRoute.Content} />
      <Route path={`${basepath}/remove`} component={RemoveRoute.Content} />
      <Route path={`${basepath}/edit`} component={EditRoute.Content} />
      <Route path={`${basepath}`} component={ViewRoute.Content} />
    </Switch>
  );
}

// The id is generated and says nothing; the description is what the bucket is
// called everywhere else on the page.
function BucketCrumb({ base, bucketId }) {
  const label = useCrumbLabel({
    query: QueriesQL.get,
    variables: { bucketId },
    datapath: "buckets.get.description",
    fallback: bucketId,
  });

  return <Crumb to={`${base}`} label={label} />;
}

export function Indice(props) {
  return (
    <LocalProvider {...props}>
      {({ base, basepath, params: { bucketId } }) => (
        <>
          <BucketCrumb base={base} bucketId={bucketId} />

          <Switch>
            <Route path={`${basepath}/file`} component={FilesRoute.Indice} />
            <Route path={`${basepath}/statistics`} component={StatisticsRoute.Indice} />
            <Route path={`${basepath}/remove`} component={RemoveRoute.Indice} />
            <Route path={`${basepath}/edit`} component={EditRoute.Indice} />
            <Route path={`${basepath}`} component={ViewRoute.Indice} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}

export const Sidebar = () => (
  <>
    <EditRoute.Sidebar />
    <ViewRoute.Sidebar />
    <StatisticsRoute.Sidebar />
  </>
);
